import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaSave, FaRupeeSign } from "react-icons/fa";
import { databaseURL } from "./firebase";
import { SUBSCRIPTION_PLANS, normalizePlanPrices } from "./plans";

const firebaseURL = `${databaseURL}/Admin/purchesPlan.json`;

function PurchesPlan() {
  const [plansData, setPlansData] = useState(() => normalizePlanPrices());

  const [isSaving, setIsSaving] = useState(false);

  // Fetch existing plans from Firebase
  useEffect(() => {
    const fetchPurchesPlan = async () => {
      try {
        const response = await fetch(firebaseURL);
        if (!response.ok) throw new Error("Failed to fetch purchase plans");

        const data = await response.json();
        if (data) {
          setPlansData(normalizePlanPrices(data));
        }
      } catch (error) {
        console.error("Error fetching purchase plan:", error);
      }
    };

    fetchPurchesPlan();
  }, []);
  
  // Handle input changes
  const handleInputChange = (plan, field, value) => {
    setPlansData((prev) => ({
      ...prev,
      [plan]: { ...prev[plan], [field]: value },
    }));
  };

  // Save data to Firebase
  const savePurchesPlan = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(firebaseURL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(plansData),
      });

      if (response.ok) {
        alert("Subscription plans saved successfully!");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to save subscription plans.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        maxWidth: "900px",
        margin: "40px auto",
        padding: "30px",
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
        fontFamily: "'Poppins', sans-serif"
      }}
    >
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "30px",
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "20px"
      }}>
        <h1 style={{
          fontSize: "28px",
          fontWeight: "700",
          color: "#2d3748",
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <FaRupeeSign style={{ color: "#4CAF50" }} />
         Set Subscription Plans
        </h1>
      </div>
      
      <div style={{ marginBottom: "35px" }}>
        <h3 style={{
          color: "#4CAF50",
          marginBottom: "20px",
          fontSize: "20px",
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}>
          Student-Limit Subscription Plans
        </h3>
        
        <div style={{
          overflow: "hidden",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.03)"
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc" }}>
                <th style={{
                  padding: "16px",
                  borderBottom: "1px solid #e2e8f0",
                  textAlign: "left",
                  color: "#4a5568",
                  fontWeight: "600",
                  fontSize: "15px"
                }}>Plan</th>
                <th style={{
                  padding: "16px",
                  borderBottom: "1px solid #e2e8f0",
                  textAlign: "center",
                  color: "#4a5568",
                  fontWeight: "600",
                  fontSize: "15px"
                }}>Student Limit</th>
                <th style={{
                  padding: "16px",
                  borderBottom: "1px solid #e2e8f0",
                  textAlign: "center",
                  color: "#4a5568",
                  fontWeight: "600",
                  fontSize: "15px"
                }}>Price (₹)</th>
              </tr>
            </thead>
            <tbody>
              {SUBSCRIPTION_PLANS.map((plan) => (
                <motion.tr 
                  key={plan.name}
                  whileHover={{ backgroundColor: "#f8f9fa" }}
                  transition={{ duration: 0.2 }}
                  style={{ borderBottom: "1px solid #e2e8f0" }}
                >
                  <td style={{
                    padding: "16px",
                    fontWeight: "500",
                    color: "#2d3748",
                    fontSize: "15px"
                  }}>
                    {plan.name}
                  </td>
                  <td style={{ padding: "16px", textAlign: "center", color: "#4a5568" }}>
                    {plan.studentLimit.toLocaleString()} students
                    {plan.durationDays ? ` / ${plan.durationDays} days` : " / year"}
                  </td>
                  <td style={{ padding: "16px", textAlign: "center" }}>
                    <motion.div
                      whileFocus={{ 
                        boxShadow: "0 0 0 2px rgba(74, 205, 100, 0.5)",
                        scale: 1.02
                      }}
                      style={{
                        display: "inline-block",
                        position: "relative"
                      }}
                    >
                      <span style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#718096"
                      }}>₹</span>
                      <input
                        type="number"
                        value={plansData[plan.name]?.mrp ?? plan.price}
                        onChange={(e) => handleInputChange(plan.name, "mrp", e.target.value)}
                        disabled={plan.studentLimit === 20}
                        aria-label={`${plan.name} annual price`}
                        style={{
                          width: "140px",
                          padding: "12px 12px 12px 35px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          outline: "none",
                          fontSize: "15px",
                          transition: "all 0.2s",
                          background: "white"
                        }}
                      />
                    </motion.div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "20px",
        marginTop: "30px",
        borderTop: "1px solid #f0f0f0",
        paddingTop: "25px"
      }}>
      
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={savePurchesPlan}
          disabled={isSaving}
          style={{
            padding: "14px 30px",
            background: "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 4px 12px rgba(74, 205, 100, 0.3)",
            opacity: isSaving ? 0.7 : 1
          }}
        >
          {isSaving ? (
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1 }}
              style={{ display: "inline-block" }}
            >
              <FaSave />
            </motion.span>
          ) : (
            <FaSave />
          )}
          {isSaving ? "Saving..." : "Save Plans"}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default PurchesPlan;